/*----------------------------------
- PUBLIC REDIRECTS (SSOT)
----------------------------------*/

/**
 * @fix  docs/fixes/2026-07-18-website-legacy-marketing-redirects.md
 * @fix  docs/fixes/2026-10-04-website-product-data-page-removed.md
 * @rule Do not replace the exact-root resolver with `startsWith`, a broad regular expression, or parsed
 *       query reconstruction. Do not add the private crawler header to any of these public redirects. Do
 *       not remove the literal slash-plus-query robots allows of `/radars`, `/watchlists`, and `/projects`
 *       while their nested disallows remain. Do not change `/projects` away from `/solutions/founders`
 *       without a new product decision.
 */

import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import {
    PUBLIC_SEARCH_PRESET_LEGACY_SLUG_REDIRECTS,
    PUBLIC_SEARCH_SECTOR_LEGACY_SLUG_REDIRECTS,
    buildPublicSearchConciseMarkdownPath,
    buildPublicSearchFullMarkdownPath,
    buildPublicSearchHumanPath,
    buildPublicSearchPresetHumanPath,
} from '@uniquedomains/core/domains/public-search';

/*----------------------------------
- TYPES
----------------------------------*/

export type WebsitePublicExactRedirect = {
    sourcePath: `/${string}`;
    destinationPath: string;
};

export type WebsitePublicSearchLegacyRedirect = {
    sourcePath: string;
    destinationPath: string;
    surface: 'human' | 'full_markdown' | 'concise_markdown';
};

type ResolveWebsitePublicRedirectInput = {
    requestUrl: string;
    toAbsoluteUrl: (pathname: string) => string;
};

/*----------------------------------
- CATALOG
----------------------------------*/

export const WEBSITE_PUBLIC_EXACT_REDIRECTS: readonly WebsitePublicExactRedirect[] = [
    { sourcePath: '/radars', destinationPath: LANDING_WEBSITE_PAGE_ROUTES.productRadars },
    { sourcePath: '/watchlists', destinationPath: LANDING_WEBSITE_PAGE_ROUTES.productWatchlists },
    { sourcePath: '/projects', destinationPath: LANDING_WEBSITE_PAGE_ROUTES.solutionFounders },
    /* The AI Connector (MCP) was removed on 2026-08-28. Both `/mcp` and its machine
       companion `/mcp-context` were indexed, linked from external write-ups, and
       quoted by assistants, so they keep answering instead of turning into 404s.
       The API page is the destination because it is the only programmatic surface
       left, even while that API itself is still marked coming soon. */
    { sourcePath: '/mcp', destinationPath: LANDING_WEBSITE_PAGE_ROUTES.apiDocs },
    { sourcePath: '/mcp-context', destinationPath: LANDING_WEBSITE_PAGE_ROUTES.apiDocs },
    /* The blog is gone and no domain-authority page replaced it, yet this article still
       drew 5 entry sessions in the 14 days before 2026-09-19. Verify is the page about
       judging a name before buying it, which is what the article was about. */
    { sourcePath: '/blog/domain-authority-why-does-matter', destinationPath: LANDING_WEBSITE_PAGE_ROUTES.productVerify },
    /* The data page and its full Markdown mirror were removed on 2026-10-04. The scanner's
       GitHub READMEs linked `/product/data?utm...` for months, so both keep answering; the
       glossary is the destination because it defines every column the page used to explain.
       The query rides along like every other redirect here. */
    { sourcePath: '/product/data', destinationPath: LANDING_WEBSITE_PAGE_ROUTES.glossary },
    { sourcePath: '/product/data.md', destinationPath: '/ai/pages/glossary.md' },
    /* The Opportunities feature page became the daily editions on 2026-10-04 (ADR-0074): the
       feed it described is now a public page of its own, so it lands on the investors' edition. */
    { sourcePath: '/product/opportunities', destinationPath: LANDING_WEBSITE_PAGE_ROUTES.trendingDomains },
    { sourcePath: '/product/opportunities.md', destinationPath: '/trending-domains.md' },
    // The retired page's AI mirror is route-served now, so it is a site redirect like the page's.
    { sourcePath: '/ai/pages/product-opportunities.md', destinationPath: '/trending-domains.md' },
] as const;

const WEBSITE_PUBLIC_SEARCH_SECTOR_LEGACY_REDIRECTS: readonly WebsitePublicSearchLegacyRedirect[] = Object.entries(
    PUBLIC_SEARCH_SECTOR_LEGACY_SLUG_REDIRECTS,
).flatMap(([legacySlug, canonicalSlug]) => [
    {
        sourcePath: buildPublicSearchHumanPath('sector', legacySlug),
        destinationPath: buildPublicSearchHumanPath('sector', canonicalSlug),
        surface: 'human' as const,
    },
    {
        sourcePath: buildPublicSearchFullMarkdownPath('sector', legacySlug),
        destinationPath: buildPublicSearchFullMarkdownPath('sector', canonicalSlug),
        surface: 'full_markdown' as const,
    },
    {
        sourcePath: buildPublicSearchConciseMarkdownPath('sector', legacySlug),
        destinationPath: buildPublicSearchConciseMarkdownPath('sector', canonicalSlug),
        surface: 'concise_markdown' as const,
    },
]);

const WEBSITE_PUBLIC_SEARCH_PRESET_LEGACY_REDIRECTS: readonly WebsitePublicSearchLegacyRedirect[] = Object.entries(
    PUBLIC_SEARCH_PRESET_LEGACY_SLUG_REDIRECTS,
).flatMap(([legacySlug, canonicalSlug]) => {
    const sourcePath = `/domains/${encodeURIComponent(legacySlug)}`;
    const destinationPath = buildPublicSearchPresetHumanPath(canonicalSlug);

    return [
        {
            sourcePath,
            destinationPath,
            surface: 'human' as const,
        },
        {
            sourcePath: `${sourcePath}.md`,
            destinationPath: `${destinationPath}.md`,
            surface: 'full_markdown' as const,
        },
    ];
});

/* Human `/domains/*` entry paths that are neither a preset nor a sector slug rename.
   They live in this list, not in the exact list, because `/domains/*` belongs to the
   domains page route: only the priority-100 routes registered from this list run
   before it. `/domains/business` drew 2 entry sessions in the 14 days before
   2026-09-19 and answered 404. Human surface only: no `.md` twin was ever requested. */
const WEBSITE_PUBLIC_SEARCH_HUMAN_ENTRY_REDIRECTS: readonly WebsitePublicSearchLegacyRedirect[] = [
    {
        sourcePath: '/domains/business',
        destinationPath: buildPublicSearchHumanPath('sector', 'business'),
        surface: 'human',
    },
];

export const WEBSITE_PUBLIC_SEARCH_LEGACY_REDIRECTS: readonly WebsitePublicSearchLegacyRedirect[] = [
    ...WEBSITE_PUBLIC_SEARCH_SECTOR_LEGACY_REDIRECTS,
    ...WEBSITE_PUBLIC_SEARCH_PRESET_LEGACY_REDIRECTS,
    ...WEBSITE_PUBLIC_SEARCH_HUMAN_ENTRY_REDIRECTS,
];

/*----------------------------------
- HELPERS
----------------------------------*/

const findWebsitePublicRedirect = (pathname: string): WebsitePublicExactRedirect | null => {
    return (
        WEBSITE_PUBLIC_EXACT_REDIRECTS.find(
            ({ sourcePath }) => pathname === sourcePath || pathname === `${sourcePath}/`,
        ) || null
    );
};

const findWebsitePublicSearchLegacyRedirect = (pathname: string): WebsitePublicSearchLegacyRedirect | null => {
    return (
        WEBSITE_PUBLIC_SEARCH_LEGACY_REDIRECTS.find(
            ({ sourcePath }) => pathname === sourcePath || pathname === `${sourcePath}/`,
        ) || null
    );
};

/**
 * The request's query exactly as sent, `?` included, or '' when there is none.
 *
 * Every public 301 on the site carries the query through byte for byte (campaign tags, click
 * ids, GitHub intent links), so the one reader is exported rather than copied: the retired TLD
 * spoke resolver (`server/lib/tldSpokeRetirement.ts`) uses it too.
 */
export const readRawQuery = (requestUrl: string): string => {
    const queryStart = requestUrl.indexOf('?');
    if (queryStart < 0) return '';

    const fragmentStart = requestUrl.indexOf('#', queryStart);
    return requestUrl.slice(queryStart, fragmentStart < 0 ? undefined : fragmentStart);
};

export const isWebsitePublicRedirectAliasPath = (pathname: string): boolean => Boolean(findWebsitePublicRedirect(pathname));

export const resolveWebsitePublicRedirect = ({
    requestUrl,
    toAbsoluteUrl,
}: ResolveWebsitePublicRedirectInput): string | null => {
    const url = new URL(requestUrl, 'https://unique.domains');
    const redirect = findWebsitePublicRedirect(url.pathname);
    if (!redirect) return null;

    return `${toAbsoluteUrl(redirect.destinationPath)}${readRawQuery(requestUrl)}`;
};

export const resolveWebsitePublicSearchLegacyRedirect = ({
    requestUrl,
    toAbsoluteUrl,
}: ResolveWebsitePublicRedirectInput): string | null => {
    const url = new URL(requestUrl, 'https://unique.domains');
    const redirect = findWebsitePublicSearchLegacyRedirect(url.pathname);
    if (!redirect) return null;

    return `${toAbsoluteUrl(redirect.destinationPath)}${readRawQuery(requestUrl)}`;
};
